import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  jobListings: [],
  filters: {
    search: '',
    location: '',
    remote: null,
    employmentType: '',
    source: '',
    sourceExclusion: '',
    advancedOrganization: '',
    // AI Filters
    aiEmploymentType: '',
    aiWorkArrangement: '',
    aiExperienceLevel: '',
    aiHasSalary: null,
    aiTaxonomies: '',
    aiVisaSponsorship: null,
    // LinkedIn Filters
    liIndustry: '',
    liOrganizationSlug: '',
    // Other Filters
    agency: null,
    dateFilter: '',
  },
  stats: {
    total: 0,
    remote: 0,
    bySource: {},
    byEmploymentType: {},
    byAiWorkArrangement: {},
    byAiExperienceLevel: {},
    byAiTaxonomies: {},
    byLinkedInIndustry: {},
  },
  loading: false,
  error: null,
  refreshing: false,
  lastFetched: null,
  lastSearchOffset: 0,
};

export const jobListingsSlice = createSlice({
  name: 'jobListings',
  initialState,
  reducers: {
    // Set all job listings data
    setJobListings: (state, action) => {
      const jobs = Array.isArray(action.payload) ? action.payload : action.payload.jobs;
      const offset = Array.isArray(action.payload) ? 0 : (action.payload.offset ?? 0);

      state.jobListings = jobs;
      state.lastFetched = Date.now();
      state.lastSearchOffset = offset;
      state.error = null;

      // Calculate stats
      state.stats.total = jobs.length;
      state.stats.remote = jobs.filter(j => j.remote).length;

      // Count by source
      state.stats.bySource = jobs.reduce((acc, job) => {
        const source = job.source || 'Unknown';
        acc[source] = (acc[source] || 0) + 1;
        return acc;
      }, {});

      // Count by employment type
      state.stats.byEmploymentType = jobs.reduce((acc, job) => {
        const type = job.employment_type || 'Unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});

      // Count by AI work arrangement
      state.stats.byAiWorkArrangement = jobs.reduce((acc, job) => {
        const arrangement = job.ai_work_arrangement;
        if (arrangement) {
          acc[arrangement] = (acc[arrangement] || 0) + 1;
        }
        return acc;
      }, {});

      // Count by AI experience level
      state.stats.byAiExperienceLevel = jobs.reduce((acc, job) => {
        const level = job.ai_experience_level;
        if (level) {
          acc[level] = (acc[level] || 0) + 1;
        }
        return acc;
      }, {});

      // Count by AI taxonomies (count first taxonomy only)
      state.stats.byAiTaxonomies = jobs.reduce((acc, job) => {
        const taxonomies = job.ai_taxonomies_a;
        if (taxonomies && Array.isArray(taxonomies) && taxonomies.length > 0) {
          const primaryTaxonomy = taxonomies[0];
          acc[primaryTaxonomy] = (acc[primaryTaxonomy] || 0) + 1;
        }
        return acc;
      }, {});

      // Count by LinkedIn industry
      state.stats.byLinkedInIndustry = jobs.reduce((acc, job) => {
        const industry = job.linkedin_org_industry;
        if (industry) {
          acc[industry] = (acc[industry] || 0) + 1;
        }
        return acc;
      }, {});
    },

    // Set individual filter
    setFilter: (state, action) => {
      const { filterName, value } = action.payload;
      state.filters[filterName] = value;
    },

    // Clear all filters
    clearFilters: (state) => {
      state.filters = {
        search: '',
        location: '',
        remote: null,
        employmentType: '',
        source: '',
        sourceExclusion: '',
        advancedOrganization: '',
        // AI Filters
        aiEmploymentType: '',
        aiWorkArrangement: '',
        aiExperienceLevel: '',
        aiHasSalary: null,
        aiTaxonomies: '',
        aiVisaSponsorship: null,
        // LinkedIn Filters
        liIndustry: '',
        liOrganizationSlug: '',
        // Other Filters
        agency: null,
        dateFilter: '',
      };
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set refreshing state
    setRefreshing: (state, action) => {
      state.refreshing = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Reset entire state
    resetJobListings: () => {
      return initialState;
    },
  },
});

export const {
  setJobListings,
  setFilter,
  clearFilters,
  setLoading,
  setRefreshing,
  setError,
  clearError,
  resetJobListings,
} = jobListingsSlice.actions;

// Selectors
export const selectJobListings = (state) => state.jobListings.jobListings;
export const selectFilters = (state) => state.jobListings.filters;
export const selectStats = (state) => state.jobListings.stats;
export const selectLoading = (state) => state.jobListings.loading;
export const selectRefreshing = (state) => state.jobListings.refreshing;
export const selectError = (state) => state.jobListings.error;
export const selectLastFetched = (state) => state.jobListings.lastFetched;
export const selectLastSearchOffset = (state) => state.jobListings.lastSearchOffset;

export default jobListingsSlice.reducer;
